// ============================================================================
// PATRONES DIAGNÓSTICOS EXPANDIDOS - Fase 3
// Nuevos patrones: CIDP, GBS, NMM, Lambert-Eaton, atrapamientos focales,
// miopatía inflamatoria, distrofia miotónica
// ============================================================================

import { ClinicalReport } from './jsonReportGenerator';
import { NCSTestResult, SpecialStudyData, SpecialStudyTest } from '../types/ncs';
import type { DiagnosticPattern, Evidence } from '../types/diagnostic';
import {
  collectNcsIntegratedAlerts,
  correctedNcsAmplitudeForCold,
  detectSuralSparing,
  evaluateDemyelinationCriteria,
  GoldCoastRegion,
  getNerveReferenceValues,
  summarizeGoldCoastLMNRegions,
  hasNcsLowTemperatureRecords,
  LMNMuscleEvaluationInput,
  ncsStandardTemperatureC,
} from '../data/emgClinicalCriteria';

const AMG_FOREARM_DROP_THRESHOLD_PERCENT = 20;
/** PAMC mediano (estimulación codo, registro ADM) > este umbral (mV) confirma AMG (AANEM práctica común). */
const AMG_CROSSOVER_CONFIRM_MV = 0.5;
/** Por debajo de esto se considera ausencia de respuesta de crossover (bloqueo real más probable si hay caída). */
const AMG_CROSSOVER_ABSENT_MAX_MV = 0.05;

/**
 * Analizadores de patrones diagnósticos expandidos
 * Complementa los 8 patrones originales de DiagnosticPatternAnalyzer
 */
export class ExpandedPatternAnalyzer {

  private static normalizeSearchText(value: string | undefined): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private static hasMyotonicDischargesText(value: string | undefined): boolean {
    const text = this.normalizeSearchText(value);
    return /miotonic|myotonic|dive bomber/.test(text);
  }

  private static hasCRDText(value: string | undefined): boolean {
    const text = this.normalizeSearchText(value);
    return /\bcrd\b|complejas repetitivas|complex repetitive/.test(text);
  }

  private static hasToxicOrDystrophicMyopathyContext(report: ClinicalReport): boolean {
    const history = report.patient.medicalHistory;
    const clinicalContext = [
      ...history.currentMedications,
      ...history.previousDiseases,
      history.familyHistory || '',
    ]
      .map(item => this.normalizeSearchText(item))
      .join(' ');

    return /estatina|statin|colchicina|antecedentes familiares|historia familiar|familial|family history/.test(clinicalContext);
  }

  /**
   * Enfermedad de Motoneurona / sospecha ELA con reglas Awaji + Gold Coast.
   * Las fasciculaciones sólo cuentan como signo NMI cuando coexisten con PAUMs crónicos.
   */
  static analyzeMotorNeuronDiseaseAwajiGoldCoast(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    const emgMuscles = this.collectLMNMusclesFromReport(report);

    if (emgMuscles.length === 0) {
      return null;
    }

    const lmnSummary = summarizeGoldCoastLMNRegions(emgMuscles);
    const affectedRegions = lmnSummary.affectedRegions;
    const umnRegions = this.collectUMNRegionsFromClinicalSymptoms(report);

    if (lmnSummary.affectedMuscles.length > 0) {
      evidence.push({
        source: 'emg',
        parameter: 'valid_lmn_muscles_awaji',
        value: lmnSummary.affectedMuscles.map(m => `${m.muscleName} (${m.region})`),
        expectedValue: 'Denervación activa clásica o fasciculaciones + PAUMs crónicos',
        weight: 5,
        description: `${lmnSummary.affectedMuscles.length} músculos con signos NMI válidos para ELA según Awaji.`,
      });
    }

    if (affectedRegions.length >= 2) {
      evidence.push({
        source: 'emg',
        parameter: 'gold_coast_regions_with_lmn_signs',
        value: affectedRegions,
        expectedValue: '≥2 regiones',
        weight: 6,
        description: `Signos de NMI en ${affectedRegions.length} regiones Gold Coast: ${affectedRegions.join(', ')}.`,
      });
    } else if (lmnSummary.affectedMuscles.length > 0) {
      contradicting.push({
        source: 'emg',
        parameter: 'insufficient_gold_coast_regions',
        value: affectedRegions.length,
        expectedValue: '≥2 regiones',
        weight: 6,
        description: 'Los signos NMI no alcanzan dos regiones corporales distintas.',
      });
    }

    if (lmnSummary.isolatedFasciculationMuscles.length > 0) {
      contradicting.push({
        source: 'emg',
        parameter: 'isolated_fasciculations_not_counted',
        value: lmnSummary.isolatedFasciculationMuscles.map(m => m.muscleName),
        expectedValue: 'Fasciculaciones con PAUMs crónicos',
        weight: 4,
        description: 'Fasciculaciones aisladas sin cambios crónicos de PAUM; no suman para ELA.',
      });
    }

    const conductionExclusion = this.evaluateConductionBlockAndFocalNeuropathyExclusion(report);
    if (conductionExclusion.excluded) {
      evidence.push({
        source: 'ncs',
        parameter: 'no_focal_neuropathy_or_conduction_block',
        value: false,
        expectedValue: false,
        weight: 5,
        description: conductionExclusion.message,
      });
    } else {
      contradicting.push({
        source: 'ncs',
        parameter: 'possible_focal_neuropathy_or_conduction_block',
        value: true,
        expectedValue: false,
        weight: 7,
        description: conductionExclusion.message,
      });
    }

    const progressiveWeakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => s.name.includes('Debilidad'));
    if (progressiveWeakness?.present && progressiveWeakness.progression === 'worsening') {
      evidence.push({
        source: 'symptoms',
        parameter: 'progressive_weakness',
        value: 'worsening',
        expectedValue: 'worsening',
        weight: 3,
        description: 'Debilidad muscular progresiva documentada.',
      });
    }

    if (umnRegions.length > 0) {
      evidence.push({
        source: 'clinical',
        parameter: 'upper_motor_neuron_regions',
        value: umnRegions,
        expectedValue: '≥1 región con NMS o NMI en ≥3 regiones para fenotipo NMI puro',
        weight: 4,
        description: `Signos clínicos de NMS en región(es) ${umnRegions.join(', ')}.`,
      });
    } else if (affectedRegions.length >= 3) {
      evidence.push({
        source: 'clinical',
        parameter: 'pure_lmn_gold_coast_variant',
        value: affectedRegions,
        expectedValue: 'NMI en ≥3 regiones si no hay NMS documentada',
        weight: 3,
        description: 'Fenotipo puramente NMI permitido por el flujo local: signos NMI en al menos 3 regiones.',
      });
    } else {
      contradicting.push({
        source: 'clinical',
        parameter: 'missing_umn_or_three_lmn_regions',
        value: { umnRegions, lmnRegions: affectedRegions },
        expectedValue: 'NMS en ≥1 región o NMI en ≥3 regiones',
        weight: 6,
        description: 'Gold Coast no se cumple: con sólo 2 regiones NMI se requiere al menos una región con signos de NMS.',
      });
    }

    if (report.clinicalSymptoms?.sensory?.present) {
      contradicting.push({
        source: 'symptoms',
        parameter: 'sensory_symptoms_present',
        value: true,
        expectedValue: false,
        weight: 4,
        description: 'Síntomas sensitivos presentes; son atípicos para ELA y obligan a descartar miméticos.',
      });
    }

    const meetsGoldCoastTopography =
      affectedRegions.length >= 2 && (umnRegions.length >= 1 || affectedRegions.length >= 3);

    if (!meetsGoldCoastTopography || !conductionExclusion.excluded) {
      return null;
    }

    const confidence = Math.min(
      0.96,
      0.86 +
        Math.max(0, affectedRegions.length - 2) * 0.04 +
        (umnRegions.length > 0 ? 0.03 : 0) +
        (progressiveWeakness?.present && progressiveWeakness.progression === 'worsening' ? 0.03 : 0) -
        (report.clinicalSymptoms?.sensory?.present ? 0.04 : 0)
    );

    return {
      id: 'motor_neuron_disease',
      name: 'Enfermedad de Motoneurona / Sospecha de ELA',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 6 || 1),
      urgencyLevel: 'urgent',
      diagnosticCriteria: 'Awaji + Gold Coast',
    };
  }

  private static collectLMNMusclesFromReport(report: ClinicalReport): LMNMuscleEvaluationInput[] {
    const records: LMNMuscleEvaluationInput[] =
      report.electrophysiologicalData.emg?.records?.map(record => ({
        muscleOrNerveName: record.muscleOrNerveName,
        spontaneousActivity: record.spontaneousActivity,
        motorUnitPotentials: record.motorUnitPotentials,
        status: record.insertionalActivity || undefined,
      })) || [];

    const muscles: LMNMuscleEvaluationInput[] =
      report.electrophysiologicalData.emg?.muscles?.map(muscle => ({
        muscleName: muscle.muscleName,
        spontaneousActivitySummary: muscle.spontaneousActivitySummary,
        mupAmplitude: muscle.mupAmplitude,
        mupDuration: muscle.mupDuration,
        mupPolyphasia: muscle.mupPolyphasia,
      })) || [];

    return records.length > 0 ? records : muscles;
  }

  private static collectUMNRegionsFromClinicalSymptoms(report: ClinicalReport): GoldCoastRegion[] {
    const regions = new Set<GoldCoastRegion>();
    const motorSymptoms = report.clinicalSymptoms?.motor?.symptoms || [];
    const umnSymptoms = motorSymptoms.filter(symptom => {
      if (!symptom.present) return false;
      const text = [symptom.name, ...(symptom.characteristics || [])].join(' ').toLowerCase();
      return /(hiperreflexia|hyperreflexia|babinski|espastic|spastic|clonus|clono|reflejos.*aument|increased.*reflex)/i.test(text);
    });

    for (const symptom of umnSymptoms) {
      if (!symptom.locations?.length) {
        regions.add('cervical');
        continue;
      }

      for (const location of symptom.locations) {
        const normalized = location.toLowerCase();
        if (/(lengua|bulbar|facial|disartria|disfagia|jaw|mandibular)/i.test(normalized)) {
          regions.add('bulbar');
        } else if (/(brazo|mano|hombro|miembro superior|upper limb|cervical|biceps|triceps)/i.test(normalized)) {
          regions.add('cervical');
        } else if (/(tronco|torac|thorac|abdomen|abdominal)/i.test(normalized)) {
          regions.add('thoracic');
        } else if (/(pierna|pie|miembro inferior|lower limb|lumbar|lumbosacr|patelar|aquil)/i.test(normalized)) {
          regions.add('lumbosacral');
        }
      }
    }

    return [...regions];
  }

  private static evaluateConductionBlockAndFocalNeuropathyExclusion(report: ClinicalReport): {
    excluded: boolean;
    message: string;
  } {
    const results = report.electrophysiologicalData.ncs?.results || [];
    if (results.length === 0) {
      return {
        excluded: true,
        message: 'Sin evidencia NCS de neuropatía focal o bloqueo de conducción; interpretar con correlación clínica.',
      };
    }

    const suspiciousFindings = results.filter(result => {
      const text = [result.nerve, ...(result.findings || [])].join(' ').toLowerCase();
      return /(bloqueo|conduction block|focal|atrapamiento|entrapment|multifocal motor|nmm|mmn)/i.test(text);
    });

    const conductionBlocks = this.detectMotorConductionBlocks(results);

    if (suspiciousFindings.length > 0 || conductionBlocks.length > 0) {
      const sources = [
        ...suspiciousFindings.map(result => result.nerve),
        ...conductionBlocks.map(block => `${block.nerve} ${block.side} (${block.dropPercent.toFixed(1)}%)`),
      ];
      return {
        excluded: false,
        message: `Hay datos compatibles con neuropatía focal o bloqueo de conducción: ${sources.join(', ')}. Descartar NMM antes de etiquetar ELA.`,
      };
    }

    return {
      excluded: true,
      message: 'No se detectan neuropatías focales ni bloqueos de conducción motores en NCS.',
    };
  }

  private static detectMotorConductionBlocks(results: NCSTestResult[]): Array<{
    nerve: string;
    side: 'left' | 'right';
    dropPercent: number;
  }> {
    const motorResults = results.filter(result => result.type === 'motor' && result.amplitude > 0);
    const groups = new Map<string, NCSTestResult[]>();

    for (const result of motorResults) {
      const key = `${result.nerve.toLowerCase()}|${result.side}`;
      groups.set(key, [...(groups.get(key) || []), result]);
    }

    const blocks: Array<{ nerve: string; side: 'left' | 'right'; dropPercent: number }> = [];
    for (const group of groups.values()) {
      if (group.length < 2) continue;
      const sorted = [...group].sort((a, b) => a.latency - b.latency);
      const distal = sorted[0];
      const lowestProximalAmplitude = Math.min(...sorted.slice(1).map(result => result.amplitude));
      const dropPercent = ((distal.amplitude - lowestProximalAmplitude) / distal.amplitude) * 100;
      if (dropPercent >= 30) {
        blocks.push({ nerve: distal.nerve, side: distal.side, dropPercent });
      }
    }

    return blocks;
  }

  /**
   * Aviso de consistencia: caída cubital muñeca→codo sin estudio de Martin-Gruber / crossover.
   * Útil en formularios antes de cerrar NCS.
   */
  static getMartinGruberWorkflowWarning(
    ncsResults: NCSTestResult[],
    specialStudies?: SpecialStudyData
  ): string | null {
    const medianElbowGreater = this.hasMedianElbowGreaterThanWrist(ncsResults);
    const tests = specialStudies?.notPerformed ? [] : specialStudies?.tests;
    const hasCrossoverTest = tests?.some(t => /martin|gruber|crossover|pamcMedianStimElbowAdm/i.test([t.name, ...Object.keys(t.values || {})].join(' ')));
    if (medianElbowGreater && !hasCrossoverTest) {
      return 'La amplitud motora mediana proximal supera a la distal. Confirme o descarte anastomosis Martin-Gruber antes de interpretar atrapamiento mediano/cubital o bloqueo de conducción.';
    }

    const r = this.evaluateUlnarMartinGruberFromData(ncsResults, specialStudies);
    if (r.missingCrossoverTest) {
      return 'Se detectó caída de amplitud en N. Cubital (muñeca → debajo del codo). Realice el test de Martin-Gruber / crossover mediano-cubital (PAMC mediano en codo registrando en ADM) para descartar pseudo-bloqueo antes de interpretar bloqueo desmielinizante.';
    }
    return null;
  }

  private static hasMedianElbowGreaterThanWrist(ncsResults: NCSTestResult[]): boolean {
    const sides: Array<'left' | 'right'> = ['left', 'right'];
    for (const side of sides) {
      const median = ncsResults.filter(r =>
        r.type === 'motor' &&
        r.side === side &&
        /mediano|median/i.test(r.nerve) &&
        r.amplitude > 0
      );
      const wrist = median.find(r => /muñeca|muneca|wrist/i.test([r.nerve, r.stimulationSite || ''].join(' ')));
      const elbow = median.find(r => /codo|elbow/i.test([r.nerve, r.stimulationSite || ''].join(' ')));
      if (wrist && elbow && elbow.amplitude > wrist.amplitude * 1.2) return true;
      if (median.some(r => (r.proximalAmplitude || 0) > r.amplitude * 1.2)) return true;
    }
    return false;
  }

  /** Aviso si hay NCS con temperatura registrada bajo el estándar del territorio (32 °C MMSS / 30 °C MMII). */
  static getThermalNcsPrecisionWarning(ncsResults: NCSTestResult[]): string | null {
    if (!hasNcsLowTemperatureRecords(ncsResults)) return null;
    return 'Se detectaron registros NCS con temperatura cutánea por debajo del estándar (32 °C en miembro superior, 30 °C en inferior). El sistema puede aplicar correcciones; si los valores son limítrofes, caliente la extremidad y repita la prueba.';
  }

  /**
   * Verifica si una caída de amplitud en el cubital es pseudo-bloqueo por AMG o bloqueo real.
   * Porcentaje de caída: (A_muñeca − A_bajo_codo) / A_muñeca × 100 (referencia distal = muñeca).
   */
  private static isUlnarPseudoBlockAMG(report: ClinicalReport): ReturnType<typeof ExpandedPatternAnalyzer.evaluateUlnarMartinGruberFromData> {
    const motorResults = report.electrophysiologicalData.ncs?.results || [];
    const special = report.electrophysiologicalData.specialStudies;
    return this.evaluateUlnarMartinGruberFromData(motorResults, special);
  }

  private static evaluateUlnarMartinGruberFromData(
    motorResults: NCSTestResult[],
    special?: SpecialStudyData
  ): {
    isAMG: boolean;
    suggestsRealBlock: boolean;
    missingCrossoverTest: boolean;
    confidence: number;
    message: string;
  } {
    const tests = special?.notPerformed ? [] : special?.tests;
    const sides: Array<'left' | 'right'> = ['left', 'right'];
    let best: { side: 'left' | 'right'; dropPercent: number } | null = null;

    for (const side of sides) {
      const pair = this.pairUlnarWristBelowElbow(motorResults, side);
      if (!pair) continue;
      if (pair.dropPercent > AMG_FOREARM_DROP_THRESHOLD_PERCENT) {
        if (!best || pair.dropPercent > best.dropPercent) {
          best = { side, dropPercent: pair.dropPercent };
        }
      }
    }

    if (!best) {
      return {
        isAMG: false,
        suggestsRealBlock: false,
        missingCrossoverTest: false,
        confidence: 0,
        message: 'Sin caída significativa en antebrazo cubital (>20% muñeca vs bajo codo).',
      };
    }

    const { side, dropPercent } = best;
    const crossover = this.findCrossoverMartinGruberStudyForSide(tests, side);
    const cmapMv = crossover ? this.extractCrossoverCmapMv(crossover) : null;

    if (!crossover || cmapMv === null) {
      return {
        isAMG: false,
        suggestsRealBlock: false,
        missingCrossoverTest: true,
        confidence: 0.4,
        message: `Caída de amplitud ${dropPercent.toFixed(1)}% en cubital (${side}). Datos incompletos: falta estudio Martin-Gruber / crossover con PAMC mediano (codo → ADM) cuantificado.`,
      };
    }

    if (crossover.crossoverResult === 'positive' || cmapMv > AMG_CROSSOVER_CONFIRM_MV) {
      return {
        isAMG: true,
        suggestsRealBlock: false,
        missingCrossoverTest: false,
        confidence: 0.95,
        message: `Caída ${dropPercent.toFixed(1)}% explicada por anastomosis Martin-Gruber (PAMC crossover ${cmapMv.toFixed(2)} mV).`,
      };
    }

    if (crossover.crossoverResult === 'negative' || cmapMv <= AMG_CROSSOVER_ABSENT_MAX_MV) {
      return {
        isAMG: false,
        suggestsRealBlock: true,
        missingCrossoverTest: false,
        confidence: 0.85,
        message: `Caída significativa sin evidencia de AMG (crossover ≤${AMG_CROSSOVER_ABSENT_MAX_MV} mV o negativo). Sospecha de bloqueo de conducción real.`,
      };
    }

    return {
      isAMG: false,
      suggestsRealBlock: false,
      missingCrossoverTest: false,
      confidence: 0.35,
      message: `Caída ${dropPercent.toFixed(1)}%; resultado de Martin-Gruber no concluyente (PAMC ${cmapMv.toFixed(2)} mV).`,
    };
  }

  private static normalizedNCSSite(r: NCSTestResult): string | undefined {
    const raw = r.site ?? r.stimulationSite;
    if (!raw) return undefined;
    if (raw === 'elbow') return 'below_elbow';
    return raw;
  }

  private static isUlnarMotorName(nerve: string): boolean {
    const n = nerve.toLowerCase();
    return n.includes('ulnar') || n.includes('cubital');
  }

  /** Caída relativa respecto a la referencia distal (muñeca): (distal − proximal) / distal × 100. */
  private static ulnarForearmAmplitudeDropPercent(distalAmp: number, proximalAmp: number): number | null {
    if (!(distalAmp > 0) || !Number.isFinite(distalAmp) || !Number.isFinite(proximalAmp)) {
      return null;
    }
    return ((distalAmp - proximalAmp) / distalAmp) * 100;
  }

  private static pairUlnarWristBelowElbow(
    results: NCSTestResult[],
    side: 'left' | 'right'
  ): { wrist: NCSTestResult; belowElbow: NCSTestResult; dropPercent: number } | null {
    const ulnarMotor = results.filter(
      r => r.type === 'motor' && r.side === side && this.isUlnarMotorName(r.nerve)
    );

    let wrist = ulnarMotor.find(r => this.normalizedNCSSite(r) === 'wrist');
    let belowElbow = ulnarMotor.find(r => {
      const s = this.normalizedNCSSite(r);
      return s === 'below_elbow';
    });

    if ((!wrist || !belowElbow) && ulnarMotor.length === 2) {
      const sorted = [...ulnarMotor].sort((a, b) => a.latency - b.latency);
      if (!wrist) wrist = sorted[0];
      if (!belowElbow) belowElbow = sorted[1];
    }

    if (!wrist || !belowElbow || wrist.id === belowElbow.id) {
      return null;
    }

    const dropPercent = this.ulnarForearmAmplitudeDropPercent(wrist.amplitude, belowElbow.amplitude);
    if (dropPercent === null) return null;
    return { wrist, belowElbow, dropPercent };
  }

  private static ncsNerveSideKey(result: NCSTestResult): string {
    return `${result.nerve.toLowerCase()}|${result.side}`;
  }

  private static getDistalMotorAmplitudeByNerve(results: NCSTestResult[]): Map<string, number> {
    const groups = new Map<string, NCSTestResult[]>();

    for (const result of results) {
      if (result.type !== 'motor' || !(result.amplitude > 0)) continue;
      const key = this.ncsNerveSideKey(result);
      groups.set(key, [...(groups.get(key) || []), result]);
    }

    const distalAmplitudes = new Map<string, number>();
    for (const [key, group] of groups.entries()) {
      const distal = group.find(result => {
        const site = this.normalizedNCSSite(result);
        return site === 'wrist' || result.stimulationSite === 'ankle';
      }) || [...group].sort((a, b) => a.latency - b.latency)[0];

      distalAmplitudes.set(key, distal.amplitude);
    }

    return distalAmplitudes;
  }

  private static isCrossoverMartinGruberStudy(test: SpecialStudyTest): boolean {
    const n = test.name.toLowerCase();
    const id = (test.id || '').toLowerCase();
    return (
      (n.includes('martin') && n.includes('gruber')) ||
      n.includes('crossover') ||
      n.includes('median-to-ulnar') ||
      n.includes('entrecruzamiento') ||
      id.includes('martin_gruber') ||
      id.includes('martin-gruber')
    );
  }

  private static extractCrossoverCmapMv(test: SpecialStudyTest): number | null {
    if (typeof test.amplitude === 'number' && Number.isFinite(test.amplitude)) {
      return test.amplitude;
    }
    const v = test.values || {};
    const preferredKeys = [
      'pamcMedianStimElbowAdm',
      'medianStimElbowCmapAdm',
      'pamc',
      'cmap',
      'amplitude',
    ];
    for (const k of preferredKeys) {
      if (k in v && typeof (v as Record<string, number>)[k] === 'number') {
        const n = (v as Record<string, number>)[k];
        if (Number.isFinite(n)) return n;
      }
    }
    const nums = Object.values(v)
      .map(x => Number(x))
      .filter(x => Number.isFinite(x));
    if (nums.length === 1) return nums[0];
    return null;
  }

  private static findCrossoverMartinGruberStudyForSide(
    tests: SpecialStudyTest[] | undefined,
    side: 'left' | 'right'
  ): SpecialStudyTest | undefined {
    if (!tests?.length) return undefined;
    return tests.find(t => {
      if (!this.isCrossoverMartinGruberStudy(t)) return false;
      if (t.side === 'bilateral') return true;
      return t.side === side;
    });
  }

  /**
   * Evalúa si hay compromiso sensitivo (SNAP) en el territorio dermatomal.
   * Si hay SNAP anormal, la confianza en radiculopatía pura debe bajar drásticamente (plexo más probable).
   */
  static getSensoryInconsistencyScore(
    report: ClinicalReport,
    suspectedRoot: string
  ): { hasAbnormalSNAP: boolean; hasDocumentedNormalSNAP: boolean; hasRelevantSNAP: boolean; description: string } {
    const ncs = report.electrophysiologicalData?.ncs;
    if (!ncs?.results?.length) {
      return { hasAbnormalSNAP: false, hasDocumentedNormalSNAP: false, hasRelevantSNAP: false, description: '' };
    }

    const rootToSensoryMap: Record<string, string[]> = {
      C5: ['lateral antebrachial cutaneous'],
      C6: ['median', 'radial', 'lateral antebrachial cutaneous'],
      C7: ['median'],
      C8: ['ulnar', 'medial antebrachial cutaneous', 'dorsal ulnar cutaneous'],
      L4: ['saphenous'],
      L5: ['superficial peroneal'],
      S1: ['sural'],
    };

    const key = suspectedRoot.trim().toUpperCase();
    const relevantNerves = rootToSensoryMap[key] || [];
    if (relevantNerves.length === 0) {
      return { hasAbnormalSNAP: false, hasDocumentedNormalSNAP: false, hasRelevantSNAP: false, description: '' };
    }

    const relevantResults = ncs.results.filter(r => {
      if (r.type !== 'sensory') return false;
      const nerveName = (r.nerve || '').toLowerCase();
      return relevantNerves.some(n => nerveName.includes(n));
    });
    const abnormalResults = relevantResults.filter(r => r.status === 'abnormal');

    if (abnormalResults.length > 0) {
      return {
        hasAbnormalSNAP: true,
        hasDocumentedNormalSNAP: false,
        hasRelevantSNAP: true,
        description: `SNAP anormal detectado en ${abnormalResults.map(r => r.nerve).join(', ')}. Esto contradice una radiculopatía pura y sugiere plexopatía.`,
      };
    }

    if (relevantResults.some(r => r.status === 'normal')) {
      return {
        hasAbnormalSNAP: false,
        hasDocumentedNormalSNAP: true,
        hasRelevantSNAP: true,
        description: `SNAP sensitivo relevante documentado como normal (${relevantResults.map(r => r.nerve).join(', ')}), consistente con lesión preganglionar/radiculopatía si la EMG miotómica lo apoya.`,
      };
    }

    return {
      hasAbnormalSNAP: false,
      hasDocumentedNormalSNAP: false,
      hasRelevantSNAP: false,
      description: `No hay SNAP sensitivo relevante documentado para ${key}; completar neuroconducción sensitiva antes de asumir lesión preganglionar pura.`,
    };
  }

  /**
   * Ejecutar todos los analizadores expandidos
   */
  static analyzeExpandedPatterns(report: ClinicalReport): DiagnosticPattern[] {
    const patterns: DiagnosticPattern[] = [];

    const analyzers = [
      this.analyzeNcsModernCriteriaAlerts,
      this.analyzeCIDP,
      this.analyzeAxonalColdMaskedNCS,
      this.analyzeGBS,
      this.analyzeMultifocalMotorNeuropathy,
      this.analyzeLambertEaton,
      this.analyzeUlnarNeuropathyElbow,
      this.analyzePeronealNeuropathy,
      this.analyzeInflammatoryMyopathy,
      this.analyzeMyotonicDystrophy,
      this.analyzeSmallFiberNeuropathy,
    ];

    analyzers.forEach(analyzer => {
      const pattern = analyzer.call(ExpandedPatternAnalyzer, report);
      if (pattern && pattern.confidence > 0.3) {
        patterns.push(pattern);
      }
    });

    return patterns.sort((a, b) => b.confidence - a.confidence);
  }

  /**
   * Umbrales de duración CMAP, Martin-Gruber mediano, sural sparing y sospecha de polaridad invertida.
   */
  private static analyzeNcsModernCriteriaAlerts(report: ClinicalReport): DiagnosticPattern | null {
    const results = report.electrophysiologicalData?.ncs?.results;
    if (!results?.length) return null;

    const alerts = collectNcsIntegratedAlerts(results);
    if (alerts.length === 0) return null;

    const evidence: Evidence[] = alerts.map(a => ({
      source: 'ncs' as const,
      parameter: a.id,
      value: true,
      expectedValue: false,
      weight: a.urgency === 'urgent' || a.urgency === 'high' ? 4 : 3,
      description: a.message,
    }));

    let confidence = 0.32 + Math.min(0.22, alerts.length * 0.045);
    confidence = Math.min(0.74, confidence);

    const urgentish = alerts.some(a => a.urgency === 'high' || a.urgency === 'urgent');

    return {
      id: 'ncs_modern_criteria_alerts',
      name: 'Hallazgos NCS (criterios EAN/PNS y técnica)',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: [],
      clinicalRelevance: Math.min(1, evidence.reduce((s, e) => s + e.weight, 0) / (evidence.length * 5 || 1)),
      urgencyLevel: urgentish ? 'high' : 'medium',
      diagnosticCriteria: 'Duración CMAP; Martin-Gruber; sural sparing; revisión polaridad',
    };
  }

  // ============================================================================
  // Patrón axonal con corrección térmica (frío enmascara pérdida de PAMC)
  // ============================================================================
  private static analyzeAxonalColdMaskedNCS(report: ClinicalReport): DiagnosticPattern | null {
    const results = report.electrophysiologicalData?.ncs?.results || [];
    const evidence: Evidence[] = [];
    const MOTOR_CMAP_LOW_MV = 3.0;
    let coldLowAmpNerves = 0;

    for (const r of results) {
      if (r.type !== 'motor' || !(r.amplitude > 0)) continue;
      const { corrected, applied, standard } = correctedNcsAmplitudeForCold(r.amplitude, r.temperature, r.nerve);
      if (!applied) continue;
      if (corrected < MOTOR_CMAP_LOW_MV) {
        coldLowAmpNerves++;
        evidence.push({
          source: 'ncs',
          parameter: 'cold_masked_axonal_loss',
          value: corrected,
          expectedValue: `≥ ${MOTOR_CMAP_LOW_MV} mV`,
          weight: 3,
          description: `Pérdida axonal sugerida tras corrección por temperatura: PAMC ${corrected.toFixed(2)} mV en ${r.nerve} (registro a ${r.temperature}°C < ${standard}°C).`,
        });
      }
    }

    if (coldLowAmpNerves === 0) return null;

    let confidence = 0.42 + (coldLowAmpNerves - 1) * 0.12;
    confidence = Math.min(0.78, confidence);

    return {
      id: 'axonal_cold_temperature_corrected',
      name: 'Compromiso axonal (NCS con corrección térmica)',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: [],
      clinicalRelevance: evidence.reduce((s, e) => s + e.weight, 0) / (evidence.length * 5 || 1),
      urgencyLevel: 'medium',
      diagnosticCriteria: 'PAMC corregida 2.5%/°C bajo estándar territorial',
    };
  }

  // ============================================================================
  // CIDP - Polineuropatía Desmielinizante Inflamatoria Crónica
  // Criterios: EFNS/PNS 2021
  // ============================================================================
  private static analyzeCIDP(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Síntomas: debilidad progresiva simétrica >8 semanas
    const weakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Debilidad') || s.name.includes('Weakness')
    );
    if (weakness?.present) {
      const isProximalAndDistal = weakness.locations?.some(l => 
        l.toLowerCase().includes('proximal')
      ) && weakness.locations?.some(l => 
        l.toLowerCase().includes('distal')
      );
      if (isProximalAndDistal) {
        evidence.push({
          source: 'symptoms',
          parameter: 'proximal_distal_weakness',
          value: true,
          expectedValue: true,
          weight: 3,
          description: 'Debilidad proximal y distal (típico CIDP)'
        });
        confidence += 0.2;
      }
    }

    // Criterios electrofisiológicos EFNS/PNS:
    if (report.electrophysiologicalData?.ncs) {
      const amgValidation = this.isUlnarPseudoBlockAMG(report);

      if (amgValidation.isAMG) {
        contradicting.push({
          source: 'ncs',
          parameter: 'amg_detected',
          value: true,
          expectedValue: false,
          weight: 5,
          description: amgValidation.message,
        });
        confidence -= 0.4;
      } else if (amgValidation.suggestsRealBlock) {
        evidence.push({
          source: 'ncs',
          parameter: 'true_conduction_block',
          value: true,
          expectedValue: true,
          weight: 5,
          description: `Bloqueo de conducción apoyado tras descartar AMG: ${amgValidation.message}`,
        });
        confidence += 0.3;
      }

      const motorResults = report.electrophysiologicalData.ncs.results.filter(r => 
        r.type === 'motor'
      );

      const demyelinatingNerveKeys = new Set<string>();
      const distalAmplitudeByNerve = this.getDistalMotorAmplitudeByNerve(motorResults);

      motorResults.forEach(result => {
        const stdTemp = ncsStandardTemperatureC(result.nerve);
        const temp = result.temperature;
        const isCold =
          temp !== undefined && temp !== null && Number.isFinite(temp) && temp < stdTemp;
        const nerveKey = this.ncsNerveSideKey(result);
        const reference = getNerveReferenceValues(result);

        if (!reference) {
          contradicting.push({
            source: 'ncs',
            parameter: 'missing_nerve_reference_values',
            value: result.nerve,
            expectedValue: 'LLN/ULN por nervio',
            weight: 2,
            description: `No hay valores de referencia suficientes para ${result.nerve}; no se evalúa como criterio desmielinizante EFNS/PNS.`,
          });
          return;
        }

        if (isCold) {
          if (result.latency || result.velocity) {
            contradicting.push({
              source: 'ncs',
              parameter: 'cold_artifact_demyelinating',
              value: temp,
              expectedValue: `T ≥ ${stdTemp}°C para criterio desmielinizante`,
              weight: 4,
              description: `${result.nerve} registrado con piel fría (${temp}°C): posible prolongación/enlentecimiento térmico; no contado como desmielinización difusa.`,
            });
            confidence -= 0.06;
          }
          return;
        }

        const distalAmplitude = distalAmplitudeByNerve.get(nerveKey) ?? result.amplitude;
        const demyelinationCheck = evaluateDemyelinationCriteria(
          distalAmplitude,
          result.velocity,
          result.latency,
          reference
        );

        if (demyelinationCheck.isUninterpretable) {
          contradicting.push({
            source: 'ncs',
            parameter: 'uninterpretable_severe_axonal_loss',
            value: distalAmplitude,
            expectedValue: '≥ 0.5 mV',
            weight: 4,
            description: `Amplitud distal críticamente baja en ${result.nerve} (${distalAmplitude.toFixed(2)} mV). La latencia/VCN no son fiables para diagnosticar desmielinización primaria.`,
          });
          confidence -= 0.1;
          return;
        }

        let hasDemyelinatingCriterion = false;

        if (demyelinationCheck.isLatencyDemyelinating) {
          hasDemyelinatingCriterion = true;
          evidence.push({
            source: 'ncs',
            parameter: 'demyelinating_latency_dynamic_efns_pns',
            value: result.latency,
            expectedValue: `> ${demyelinationCheck.thresholdsUsed.latency.toFixed(1)} ms`,
            weight: 4,
            description: `Latencia prolongada en ${result.nerve} ajustada por amplitud distal (${distalAmplitude.toFixed(2)} mV; LLN ${reference.amplitudeLLN} mV): ${result.latency} ms > ${demyelinationCheck.thresholdsUsed.latency.toFixed(1)} ms (${demyelinationCheck.hasAmplitudeDependentAxonalLoss ? 'umbral estricto por pérdida axonal' : 'umbral estándar'}).`,
          });
        }

        if (demyelinationCheck.isVelocityDemyelinating) {
          hasDemyelinatingCriterion = true;
          evidence.push({
            source: 'ncs',
            parameter: 'demyelinating_velocity_dynamic_efns_pns',
            value: result.velocity,
            expectedValue: `< ${demyelinationCheck.thresholdsUsed.velocity.toFixed(1)} m/s`,
            weight: 4,
            description: `Velocidad reducida en ${result.nerve} ajustada por amplitud distal (${distalAmplitude.toFixed(2)} mV; LLN ${reference.amplitudeLLN} mV): ${result.velocity} m/s < ${demyelinationCheck.thresholdsUsed.velocity.toFixed(1)} m/s (${demyelinationCheck.hasAmplitudeDependentAxonalLoss ? 'umbral estricto por pérdida axonal' : 'umbral estándar'}).`,
          });
        }

        if (hasDemyelinatingCriterion && !demyelinatingNerveKeys.has(nerveKey)) {
          demyelinatingNerveKeys.add(nerveKey);
          confidence += 0.15;
        }
      });

      // Se requieren hallazgos desmielinizantes en ≥2 nervios
      if (demyelinatingNerveKeys.size >= 2) {
        evidence.push({
          source: 'ncs',
          parameter: 'multiple_demyelinating_nerves',
          value: demyelinatingNerveKeys.size,
          expectedValue: '≥2',
          weight: 5,
          description: `Hallazgos desmielinizantes dinámicos EFNS/PNS en ${demyelinatingNerveKeys.size} nervios motores únicos.`
        });
        confidence += 0.3;
      }
    }

    // Evidencia contradictoria
    // Curso agudo (<4 semanas) contradice CIDP (sugiere GBS)
    // Usar onset de síntomas motores como proxy para timeline
    const motorOnset = report.clinicalSymptoms?.motor?.symptoms?.find(s => s.present)?.onset;
    if (motorOnset && typeof motorOnset === 'string' && motorOnset.toLowerCase().includes('agudo')) {
      contradicting.push({
        source: 'clinical',
        parameter: 'acute_onset',
        value: true,
        expectedValue: false,
        weight: 3,
        description: 'Inicio agudo (<4 semanas) es más compatible con GBS que CIDP'
      });
      confidence -= 0.2;
    }

    // CIDP requires additional clinical workup (LCR, biopsy) for certainty > 92%
    confidence = Math.min(0.92, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'cidp',
      name: 'Polineuropatía Desmielinizante Inflamatoria Crónica (CIDP)',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 5),
      urgencyLevel: confidence > 0.7 ? 'high' : 'medium',
      diagnosticCriteria: 'EFNS/PNS 2021'
    } : null;
  }

  // ============================================================================
  // GBS - Síndrome de Guillain-Barré
  // Criterios: Brighton + AANEM
  // ============================================================================
  private static analyzeGBS(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Debilidad progresiva ascendente bilateral
    const weakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Debilidad')
    );
    if (weakness?.present) {
      evidence.push({
        source: 'symptoms',
        parameter: 'progressive_weakness',
        value: true,
        expectedValue: true,
        weight: 3,
        description: 'Debilidad progresiva bilateral'
      });
      confidence += 0.2;
    }

    // Hiporreflexia/Arreflexia
    const reflexes = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Reflejos') || s.name.includes('Hiporreflexia')
    );
    if (reflexes?.present) {
      evidence.push({
        source: 'clinical',
        parameter: 'hyporeflexia',
        value: true,
        expectedValue: true,
        weight: 4,
        description: 'Hiporreflexia o arreflexia (criterio Brighton obligatorio)'
      });
      confidence += 0.25;
    }

    // Electrofisiología: Ondas F prolongadas/ausentes
    if (report.electrophysiologicalData?.ncs) {
      const results = report.electrophysiologicalData.ncs.results;
      
      // Sural sparing: por estado (legacy) o por amplitudes (detectSuralSparing)
      const suralSnap = results.find(
        r => r.type === 'sensory' && r.nerve?.toLowerCase().includes('sural')
      );
      const upperSnaps = results.filter(r => {
        if (r.type !== 'sensory') return false;
        const nn = r.nerve?.toLowerCase() || '';
        if (/(sural|tibial|perone|fibular|saphenous|safeno)/i.test(nn)) return false;
        return /mediano|median|ulnar|cubital|radial|musculocut/i.test(nn);
      });
      const suralNormal = results.some(
        r => r.nerve?.toLowerCase().includes('sural') && r.status === 'normal'
      );
      const medianSensoryAbnormal = results.some(
        r =>
          (r.nerve?.toLowerCase().includes('median') || r.nerve?.toLowerCase().includes('mediano')) &&
          r.type === 'sensory' &&
          r.status === 'abnormal'
      );
      const amplitudeSuralSparing =
        suralSnap && upperSnaps.length > 0 && detectSuralSparing(upperSnaps, suralSnap);

      if (amplitudeSuralSparing || (suralNormal && medianSensoryAbnormal)) {
        evidence.push({
          source: 'ncs',
          parameter: 'sural_sparing_pattern',
          value: true,
          expectedValue: true,
          weight: 5,
          description: amplitudeSuralSparing
            ? 'Patrón "sural sparing" (amplitudes): SNAPs de miembro superior muy reducidos con sural conservado — muy sugestivo de GBS/AIDP'
            : 'Patrón "sural sparing" (SNAPs anormales en mediano con sural normal) — sugestivo de GBS desmielinizante',
        });
        confidence += 0.3;
      }
    }

    // Evidencia contradictoria: evolución >8 semanas sugiere CIDP
    // Asimetría marcada sugiere otra entidad

    // GBS diagnosis requires clinical + CSF correlation; NCS pattern alone cannot reach full certainty
    confidence = Math.min(0.90, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'gbs',
      name: 'Síndrome de Guillain-Barré',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 5 || 1),
      urgencyLevel: 'urgent',
      diagnosticCriteria: 'Brighton Criteria + AANEM'
    } : null;
  }

  // ============================================================================
  // NMM - Neuropatía Multifocal Motora
  // ============================================================================
  private static analyzeMultifocalMotorNeuropathy(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Debilidad asimétrica distal predominantemente motora
    const weakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Debilidad')
    );
    if (weakness?.present) {
      evidence.push({
        source: 'symptoms',
        parameter: 'asymmetric_motor_weakness',
        value: true,
        expectedValue: true,
        weight: 3,
        description: 'Debilidad motora asimétrica'
      });
      confidence += 0.2;
    }

    // NCS: Bloqueo de conducción motor con sensitivos NORMALES
    if (report.electrophysiologicalData?.ncs) {
      const motorAbnormal = report.electrophysiologicalData.ncs.results.filter(r => 
        r.type === 'motor' && r.status === 'abnormal'
      );
      const sensoryNormal = report.electrophysiologicalData.ncs.results.filter(r => 
        r.type === 'sensory' && r.status === 'normal'
      );

      if (motorAbnormal.length > 0 && sensoryNormal.length > 0) {
        evidence.push({
          source: 'ncs',
          parameter: 'motor_abnormal_sensory_normal',
          value: true,
          expectedValue: true,
          weight: 5,
          description: 'Nervios motores anormales con sensitivos normales (patrón NMM)'
        });
        confidence += 0.35;
      }
    }

    // Evidencia contradictoria: compromiso sensitivo
    const sensorySymptoms = report.clinicalSymptoms?.sensory?.present;
    if (sensorySymptoms) {
      contradicting.push({
        source: 'symptoms',
        parameter: 'sensory_involvement',
        value: true,
        expectedValue: false,
        weight: 4,
        description: 'Compromiso sensitivo es atípico en NMM'
      });
      confidence -= 0.25;
    }

    confidence = Math.min(0.88, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'multifocal_motor_neuropathy',
      name: 'Neuropatía Multifocal Motora (NMM)',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 5 || 1),
      urgencyLevel: 'high',
      diagnosticCriteria: 'EFNS/PNS criteria'
    } : null;
  }

  // ============================================================================
  // LEMS - Lambert-Eaton Myasthenic Syndrome
  // ============================================================================

  /**
   * Busca estudio de estimulación nerviosa repetitiva (RNS) y cuantifica el incremento
   * post-ejercicio. Incremento ≥100% es patognomónico de LEMS (criterio AANEM).
   */
  private static evaluateRNSFacilitation(
    specialStudies?: SpecialStudyData
  ): { hasPathologicFacilitation: boolean; incrementPercent: number | null; message: string } {
    const tests = specialStudies?.notPerformed ? [] : specialStudies?.tests;
    if (!tests?.length) {
      return { hasPathologicFacilitation: false, incrementPercent: null, message: '' };
    }

    const rnsTest = tests.find(t => {
      const name = t.name.toLowerCase();
      return /\brns\b|repetitiv|estimulaci.*repetitiv|decrement|facilita/.test(name);
    });

    if (!rnsTest) {
      return { hasPathologicFacilitation: false, incrementPercent: null, message: '' };
    }

    const values = (rnsTest.values || {}) as Record<string, number>;
    let increment: number | null = null;

    for (const key of ['postExerciseIncrement', 'increment', 'facilitation', 'postExerciseFacilitation', 'incrementoPostEjercicio']) {
      if (key in values && typeof values[key] === 'number' && Number.isFinite(values[key])) {
        increment = values[key];
        break;
      }
    }

    if (increment === null) {
      const basalCmap = values['basalCmap'] ?? values['cmap_basal'] ?? values['cmap'];
      const postCmap = values['postExerciseCmap'] ?? values['cmap_post_ejercicio'] ?? values['postCmap'];
      if (typeof basalCmap === 'number' && typeof postCmap === 'number' && basalCmap > 0) {
        increment = ((postCmap - basalCmap) / basalCmap) * 100;
      }
    }

    if (increment === null) {
      return {
        hasPathologicFacilitation: false,
        incrementPercent: null,
        message: 'Estudio RNS disponible pero sin incremento post-ejercicio cuantificado.',
      };
    }

    const hasPathologicFacilitation = increment >= 100;
    const message = hasPathologicFacilitation
      ? `Incremento post-ejercicio ≥100% (${increment.toFixed(1)}%): hallazgo patognomónico de LEMS (criterio AANEM).`
      : increment >= 50
      ? `Incremento post-ejercicio ${increment.toFixed(1)}% (limítrofe; ≥100% es el criterio patognomónico de LEMS).`
      : `Incremento post-ejercicio ${increment.toFixed(1)}% — no diagnóstico para LEMS.`;

    return { hasPathologicFacilitation, incrementPercent: increment, message };
  }

  private static analyzeLambertEaton(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Debilidad proximal
    const weakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Debilidad')
    );
    if (weakness?.present) {
      const proximal = weakness.locations?.some(l => l.toLowerCase().includes('proximal'));
      if (proximal) {
        evidence.push({
          source: 'symptoms',
          parameter: 'proximal_weakness',
          value: true,
          expectedValue: true,
          weight: 2,
          description: 'Debilidad proximal (típica LEMS)'
        });
        confidence += 0.15;
      }
    }

    // Disfunción autonómica
    const autonomic = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Autonóm') || s.name.includes('Sequedad')
    );
    if (autonomic?.present) {
      evidence.push({
        source: 'symptoms',
        parameter: 'autonomic_dysfunction',
        value: true,
        expectedValue: true,
        weight: 3,
        description: 'Disfunción autonómica (sequedad oral, hipotensión, estreñimiento)'
      });
      confidence += 0.2;
    }

    // NCS: CMAP bajo en reposo con incremento post-ejercicio
    if (report.electrophysiologicalData?.ncs) {
      const lowAmplitudeMotor = report.electrophysiologicalData.ncs.results.filter(r => 
        r.type === 'motor' && r.amplitude < 2.0
      );
      if (lowAmplitudeMotor.length > 0) {
        evidence.push({
          source: 'ncs',
          parameter: 'low_cmap_amplitude',
          value: lowAmplitudeMotor.length,
          expectedValue: '>0',
          weight: 4,
          description: 'Amplitudes CMAP basalmente bajas (patrón LEMS)'
        });
        confidence += 0.25;
      }
    }

    // RNS: Incremento ≥100% post-ejercicio es patognomónico de LEMS
    const rnsResult = this.evaluateRNSFacilitation(report.electrophysiologicalData?.specialStudies);
    if (rnsResult.incrementPercent !== null) {
      if (rnsResult.hasPathologicFacilitation) {
        evidence.push({
          source: 'special_studies',
          parameter: 'rns_post_exercise_facilitation',
          value: rnsResult.incrementPercent,
          expectedValue: '≥100%',
          weight: 8,
          description: rnsResult.message,
        });
        confidence += 0.45;
      } else if (rnsResult.incrementPercent >= 50) {
        evidence.push({
          source: 'special_studies',
          parameter: 'rns_post_exercise_facilitation_borderline',
          value: rnsResult.incrementPercent,
          expectedValue: '≥100%',
          weight: 4,
          description: rnsResult.message,
        });
        confidence += 0.15;
      } else {
        contradicting.push({
          source: 'special_studies',
          parameter: 'rns_normal_increment',
          value: rnsResult.incrementPercent,
          expectedValue: '≥100%',
          weight: 5,
          description: rnsResult.message,
        });
        confidence -= 0.2;
      }
    }

    confidence = Math.min(0.90, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'lambert_eaton',
      name: 'Síndrome Miasténico de Lambert-Eaton (LEMS)',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 5 || 1),
      urgencyLevel: 'high',
      diagnosticCriteria: 'AANEM Criteria'
    } : null;
  }

  // ============================================================================
  // Neuropatía Cubital en Codo
  // ============================================================================
  private static analyzeUlnarNeuropathyElbow(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Síntomas en territorio cubital
    const tingling = report.clinicalSymptoms?.sensory?.symptoms?.find(s => 
      s.name.includes('Hormigueo') || s.name.includes('Entumecimiento')
    );
    if (tingling?.present) {
      const ulnarTerritory = tingling.locations?.some(l => 
        l.toLowerCase().includes('4to dedo') || l.toLowerCase().includes('5to dedo') ||
        l.toLowerCase().includes('anular') || l.toLowerCase().includes('meñique') ||
        l.toLowerCase().includes('cubital')
      );
      if (ulnarTerritory) {
        evidence.push({
          source: 'symptoms',
          parameter: 'ulnar_territory_symptoms',
          value: true,
          expectedValue: true,
          weight: 3,
          description: 'Síntomas en territorio del nervio cubital (4to-5to dedos)'
        });
        confidence += 0.25;
      }
    }

    // NCS: Enlentecimiento focal a través del codo
    if (report.electrophysiologicalData?.ncs) {
      const ulnarMotor = report.electrophysiologicalData.ncs.results.filter(r => 
        r.nerve?.toLowerCase().includes('ulnar') && r.type === 'motor'
      );

      if (ulnarMotor.length > 0) {
        const abnormalUlnar = ulnarMotor.find(r => r.status === 'abnormal');
        if (abnormalUlnar) {
          evidence.push({
            source: 'ncs',
            parameter: 'ulnar_motor_abnormal',
            value: true,
            expectedValue: true,
            weight: 4,
            description: 'Conducción motora cubital anormal — evaluar segmento a través del codo'
          });
          confidence += 0.3;
        }
      }
    }

    confidence = Math.min(0.88, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'ulnar_neuropathy_elbow',
      name: 'Neuropatía Cubital en Codo',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 5 || 1),
      urgencyLevel: confidence > 0.7 ? 'medium' : 'low'
    } : null;
  }

  // ============================================================================
  // Neuropatía Peronea en Cabeza de Peroné
  // ============================================================================
  private static analyzePeronealNeuropathy(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Pie caído
    const footDrop = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Pie caído') || s.name.includes('Dorsiflexión')
    );
    if (footDrop?.present) {
      evidence.push({
        source: 'symptoms',
        parameter: 'foot_drop',
        value: true,
        expectedValue: true,
        weight: 4,
        description: 'Pie caído (debilidad de dorsiflexión)'
      });
      confidence += 0.3;
    }

    // NCS: Peroneo anormal
    if (report.electrophysiologicalData?.ncs) {
      const peroneal = report.electrophysiologicalData.ncs.results.filter(r => 
        (r.nerve?.toLowerCase().includes('peroneal') || r.nerve?.toLowerCase().includes('fibular')) &&
        r.type === 'motor'
      );

      if (peroneal.length > 0 && peroneal.some(r => r.status === 'abnormal')) {
        evidence.push({
          source: 'ncs',
          parameter: 'peroneal_abnormal',
          value: true,
          expectedValue: true,
          weight: 4,
          description: 'Conducción peronea anormal — evaluar segmento a través de cabeza de peroné'
        });
        confidence += 0.3;

        // NCS sural normal apoya neuropatía focal vs polineuropatía
        const suralNormal = report.electrophysiologicalData.ncs.results.some(r =>
          r.nerve?.toLowerCase().includes('sural') && r.status === 'normal'
        );
        if (suralNormal) {
          evidence.push({
            source: 'ncs',
            parameter: 'sural_normal',
            value: true,
            expectedValue: true,
            weight: 3,
            description: 'Sural normal — apoya neuropatía focal vs sistémica'
          });
          confidence += 0.15;
        }
      }
    }

    confidence = Math.min(0.88, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'peroneal_neuropathy',
      name: 'Neuropatía Peronea en Cabeza de Peroné',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 5 || 1),
      urgencyLevel: 'medium'
    } : null;
  }

  // ============================================================================
  // Miopatía Inflamatoria (Polimiositis / Dermatomiositis)
  // ============================================================================
  private static analyzeInflammatoryMyopathy(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Debilidad proximal simétrica
    const weakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Debilidad')
    );
    if (weakness?.present) {
      const proximal = weakness.locations?.some(l => l.toLowerCase().includes('proximal'));
      const symmetric = weakness.locations?.some(l => l.toLowerCase().includes('bilateral'));
      if (proximal) {
        evidence.push({
          source: 'symptoms',
          parameter: 'proximal_symmetric_weakness',
          value: true,
          expectedValue: true,
          weight: 3,
          description: 'Debilidad proximal simétrica (típica de miopatía inflamatoria)'
        });
        confidence += 0.2;
      }
    }

    // EMG: MUAPs cortos + fibrilaciones, excluyendo descargas miotónicas
    if (report.electrophysiologicalData?.emg) {
      const emgMuscles = report.electrophysiologicalData.emg.muscles;
      
      const myopathicWithFibs = emgMuscles.filter(r => {
        const spontaneousActivity = r.spontaneousActivitySummary;
        const spontaneousActivityText = this.normalizeSearchText(spontaneousActivity);
        const shortDuration = r.mupDuration > 0 && r.mupDuration < 8;
        const lowAmplitude = r.mupAmplitude > 0 && r.mupAmplitude < 500;
        const hasFibs = /fibrilacion|fibrillation/.test(spontaneousActivityText);
        const hasMyotonicDischarges = this.hasMyotonicDischargesText(spontaneousActivity);
        const hasCRD = this.hasCRDText(spontaneousActivity);

        if (hasMyotonicDischarges) {
          contradicting.push({
            source: 'emg',
            parameter: 'myotonic_discharges',
            value: r.muscleName,
            expectedValue: 'Ausentes',
            weight: 5,
            description: 'Las descargas miotónicas sugieren distrofia miotónica o canalopatía, no miopatía inflamatoria clásica'
          });
        }

        if (hasCRD) {
          contradicting.push({
            source: 'emg',
            parameter: 'complex_repetitive_discharges',
            value: r.muscleName,
            expectedValue: 'Ausentes o no dominantes',
            weight: 2,
            description: 'Las descargas complejas repetitivas son inespecíficas y pueden verse en miopatías crónicas; reducen la especificidad para miopatía inflamatoria activa'
          });
        }

        return (shortDuration || lowAmplitude) && hasFibs && !hasMyotonicDischarges;
      });

      if (myopathicWithFibs.length > 0) {
        evidence.push({
          source: 'emg',
          parameter: 'myopathic_with_fibrillations',
          value: myopathicWithFibs.length,
          expectedValue: '>0',
          weight: 5,
          description: 'MUAPs miopáticos con fibrilaciones — patrón activo inflamatorio/necrótico'
        });
        confidence += 0.35;
      }
    }

    if (this.hasToxicOrDystrophicMyopathyContext(report)) {
      contradicting.push({
        source: 'clinical',
        parameter: 'toxic_or_dystrophic_myopathy_context',
        value: true,
        expectedValue: false,
        weight: 3,
        description: 'Los antecedentes sugieren considerar miopatía tóxica/inducida por fármacos o distrofia muscular como diagnóstico diferencial a la miopatía inflamatoria'
      });
      confidence -= 0.15;
    }

    // Evidencia contradictoria: sin dolor muscular
    // (muchas miopatías inflamatorias cursan con mialgias)

    confidence = Math.min(0.88, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'inflammatory_myopathy',
      name: 'Miopatía Inflamatoria (Polimiositis/Dermatomiositis)',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 5 || 1),
      urgencyLevel: 'high',
      diagnosticCriteria: 'Bohan and Peter criteria'
    } : null;
  }

  // ============================================================================
  // Distrofia Miotónica
  // ============================================================================
  private static analyzeMyotonicDystrophy(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Miotonía clínica
    const myotonia = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Miotonía') || s.name.includes('Rigidez')
    );
    if (myotonia?.present) {
      evidence.push({
        source: 'symptoms',
        parameter: 'clinical_myotonia',
        value: true,
        expectedValue: true,
        weight: 4,
        description: 'Miotonía clínica (dificultad para relajar músculos)'
      });
      confidence += 0.3;
    }

    // Debilidad distal (atípica para miopatías pero típica de DM)
    const weakness = report.clinicalSymptoms?.motor?.symptoms?.find(s => 
      s.name.includes('Debilidad')
    );
    if (weakness?.present) {
      const distal = weakness.locations?.some(l => l.toLowerCase().includes('distal'));
      if (distal) {
        evidence.push({
          source: 'symptoms',
          parameter: 'distal_weakness',
          value: true,
          expectedValue: true,
          weight: 2,
          description: 'Debilidad distal (típica de distrofia miotónica, atípica en otras miopatías)'
        });
        confidence += 0.15;
      }
    }

    // EMG: Descargas miotónicas ("dive bomber")
    if (report.electrophysiologicalData?.emg) {
      const musclesWithMyotonicDischarges = report.electrophysiologicalData.emg.muscles.filter(muscle =>
        this.hasMyotonicDischargesText(muscle.spontaneousActivitySummary)
      );

      if (musclesWithMyotonicDischarges.length > 0) {
        evidence.push({
          source: 'emg',
          parameter: 'myotonic_discharges',
          value: musclesWithMyotonicDischarges.map(muscle => muscle.muscleName),
          expectedValue: '>0 músculos',
          weight: 5,
          description: 'Descargas miotónicas detectadas en EMG (marcador electrofisiológico principal)'
        });
        confidence += 0.45;
      }
    }

    // Myotonic dystrophy requires genetic confirmation for >90% certainty
    confidence = Math.min(0.90, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'myotonic_dystrophy',
      name: 'Distrofia Miotónica',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 5 || 1),
      urgencyLevel: 'medium',
      diagnosticCriteria: 'Clinical + EMG criteria'
    } : null;
  }

  // ============================================================================
  // Neuropatía de Fibras Delgadas
  // ============================================================================
  private static analyzeSmallFiberNeuropathy(report: ClinicalReport): DiagnosticPattern | null {
    const evidence: Evidence[] = [];
    const contradicting: Evidence[] = [];
    let confidence = 0;

    // Dolor neuropático/quemante distal
    const pain = report.clinicalSymptoms?.sensory?.symptoms?.find(s => 
      s.name.includes('Dolor') || s.name.includes('Quemazón')
    );
    if (pain?.present) {
      const burning = pain.characteristics?.some(c => 
        c.toLowerCase().includes('quemante') || c.toLowerCase().includes('ardor')
      );
      if (burning) {
        evidence.push({
          source: 'symptoms',
          parameter: 'burning_pain',
          value: true,
          expectedValue: true,
          weight: 3,
          description: 'Dolor quemante distal (característico de neuropatía de fibras delgadas)'
        });
        confidence += 0.25;
      }
    }

    // NCS: COMPLETAMENTE NORMAL (clave diagnóstica)
    if (report.electrophysiologicalData?.ncs) {
      const allNormal = report.electrophysiologicalData.ncs.results.every(r => 
        r.status === 'normal'
      );
      if (allNormal && report.electrophysiologicalData.ncs.results.length > 0) {
        evidence.push({
          source: 'ncs',
          parameter: 'normal_ncs',
          value: true,
          expectedValue: true,
          weight: 4,
          description: 'NCS completamente normal con síntomas sensitivos — sugiere fibras delgadas'
        });
        confidence += 0.3;
      }
    }

    // Evidencia contradictoria: NCS anormales
    if (report.electrophysiologicalData?.ncs) {
      const anyAbnormal = report.electrophysiologicalData.ncs.results.some(r => 
        r.status === 'abnormal'
      );
      if (anyAbnormal) {
        contradicting.push({
          source: 'ncs',
          parameter: 'abnormal_ncs',
          value: true,
          expectedValue: false,
          weight: 4,
          description: 'NCS anormales — la neuropatía no es exclusivamente de fibras delgadas'
        });
        confidence -= 0.3;
      }
    }

    // Small fiber neuropathy requires skin biopsy (IENFD) for definitive diagnosis
    confidence = Math.min(0.85, Math.max(0, confidence));

    return confidence > 0.3 ? {
      id: 'small_fiber_neuropathy',
      name: 'Neuropatía de Fibras Delgadas',
      confidence,
      supportingEvidence: evidence,
      contradictingEvidence: contradicting,
      clinicalRelevance: evidence.reduce((sum, e) => sum + e.weight, 0) / (evidence.length * 5 || 1),
      urgencyLevel: 'low',
      diagnosticCriteria: 'Clinical + Normal NCS + Skin biopsy'
    } : null;
  }
}
